// Token-provider + central authedFetch for the api-client.
//
// The api-client package is consumed by both marketing/ (anonymous,
// never sends a bearer) and web/ (oidc-spa drives the token store).
// Rather than depend on a specific token-store implementation, the package
// reads the current bearer through a caller-supplied getter and (optionally)
// awaits a caller-supplied readiness promise before falling back to an
// anonymous request.
//
// Wiring: the consuming app calls `configureApiClient({ getToken,
// waitForReady })` once at startup (e.g. inside <Providers />). If never
// configured, requests go out without an Authorization header — which is
// the correct behaviour for anonymous endpoints (POST /v1/anonymous/codes/render,
// GET /v1/billing/plan when not logged in, etc.).
//
// `waitForReady` lets the consuming app expose an OIDC bootstrap latch so
// the first authenticated fetch after a hard reload doesn't race the
// silent-refresh and 401. Component-level loading states (TanStack-Query
// `isPending`) cover the wait visually — no need for a route-level gate.

let _getToken: () => string | null = () => null;
let _waitForReady: (() => Promise<void>) | null = null;

export function configureApiClient(opts: {
  getToken: () => string | null;
  waitForReady?: () => Promise<void>;
}): void {
  _getToken = opts.getToken;
  _waitForReady = opts.waitForReady ?? null;
}

export function getAuthToken(): string | null {
  return _getToken();
}

/**
 * Standalone helper for callers that can't use `authedFetch` directly —
 * the FormData upload in `assets.ts` is the only such case today (it
 * needs the browser to set its own multipart Content-Type with the
 * generated boundary, and `authedFetch`'s default JSON content-type
 * would clobber that).
 *
 * Resolves immediately if no `waitForReady` was configured or the latch
 * has already flipped.
 */
export async function awaitTokenReady(): Promise<void> {
  if (_waitForReady) await _waitForReady();
}

/**
 * Wrap a `fetch` with the configured bearer header.
 *
 * If no token is available right now and the host app registered a
 * `waitForReady` callback, await that latch once (typically the OIDC
 * bootstrap completion). The retry-with-token afterwards covers the
 * hard-reload race — the first request after a reload arrives at the
 * authedFetch boundary before AuthBootstrap's `setAccessToken(...)` has
 * run, the wait gives the bootstrap a moment to finish, and a token
 * lands on the second read.
 *
 * `waitForReady` is one-shot per the token-store contract: once the
 * bootstrap is done (success or failure), every subsequent call is a
 * no-op (resolved synchronously). So this branch only kicks in on the
 * very first authedFetch after a fresh load.
 */
export async function authedFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  let token = _getToken();
  if (!token && _waitForReady) {
    await _waitForReady();
    token = _getToken();
  }
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  headers.set("Accept", "application/json, application/problem+json");
  if (init.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return fetch(path, { ...init, headers });
}
